import Phaser from "phaser";

import type { GameConfig } from "../config/gameConfig";
import {
  RelicDuelScene,
  type RelicDuelCopy,
  type RelicDuelSceneCallbacks,
} from "./RelicDuelScene";

export type RelicDuelRuntime = {
  game: Phaser.Game;
  scene: RelicDuelScene;
  ready: Promise<void>;
  resize(width: number, height: number): void;
  setReducedMotion(reducedMotion: boolean): void;
  destroy(): void;
};

// The scene is deliberately light (no physics world and a small object count),
// so a 3x cap keeps canvas text crisp on modern Telegram phones without the
// memory cost of an uncapped devicePixelRatio.
const MAX_RENDER_SCALE = 3;

export function createRelicDuelGame(
  parent: HTMLElement,
  callbacks: RelicDuelSceneCallbacks,
  config: GameConfig,
  copy: RelicDuelCopy,
  availableBalance: string,
  reducedMotion: boolean,
): RelicDuelRuntime {
  const renderScale = Math.min(MAX_RENDER_SCALE, Math.max(1, window.devicePixelRatio || 1));
  const logicalWidth = parent.clientWidth || 360;
  const logicalHeight = parent.clientHeight || 700;
  let resolveReady: () => void = () => undefined;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const scene = new RelicDuelScene(
    { ...callbacks, onReady: resolveReady },
    config,
    copy,
    availableBalance,
    reducedMotion,
    renderScale,
  );
  const game = new Phaser.Game({
    type: Phaser.CANVAS,
    parent,
    width: Math.round(logicalWidth * renderScale),
    height: Math.round(logicalHeight * renderScale),
    transparent: true,
    antialias: true,
    render: { antialias: true, pixelArt: false, roundPixels: false },
    scale: { mode: Phaser.Scale.NONE, autoCenter: Phaser.Scale.NO_CENTER },
    scene: [scene],
    banner: false,
  });
  const canvas = parent.querySelector("canvas");
  if (canvas !== null) {
    canvas.setAttribute("role", "application");
    canvas.setAttribute("aria-label", copy.canvasLabel);
    canvas.setAttribute("tabindex", "0");
  }
  return {
    game,
    scene,
    ready,
    resize(width: number, height: number): void {
      game.scale.resize(Math.round(width * renderScale), Math.round(height * renderScale));
    },
    setReducedMotion(next): void {
      scene.setReducedMotion(next);
    },
    destroy(): void {
      game.destroy(true);
    },
  };
}
